import Numbro from 'numbro'
import { useMemo } from 'react'
import { match, P } from 'ts-pattern'

import { AsyncStateN, Duration, pipe } from '@blade/std-lib'
import { Numeric } from '@blade/ui-kit'
import { type CurrencyModel } from '@root/entities/currency'
import { SuggestedGasSettings } from '@root/entities/gas-information'
import { Network } from '@root/entities/network'
import { Quote } from '@root/entities/quote'
import { useTranslation } from '@root/shared/localization'

import type { NotEnoughLiquidityError } from '@root/entities/quote/atoms'
import type { Error } from '@root/shared'
import type { ApiError } from '@root/shared/api'

export const useSwapParameters = ({
  quoteState,
  quoteCurrency,
}: {
  baseCurrency: CurrencyModel
  quoteCurrency: CurrencyModel
  quoteState: AsyncStateN.AsyncStateN<
    Error.UnknownError | ApiError.ApiError | NotEnoughLiquidityError,
    Quote.Quote
  >
}) => {
  const { t } = useTranslation()

  const averageWaitTimeState = useMemo(
    () =>
      pipe(
        quoteState,
        AsyncStateN.map(Quote.averageWaitTime),
        AsyncStateN.map((time) => {
          const seconds = Math.round(Duration.toSeconds(time))
          const formatted = `~${seconds} ${t('swap.seconds-short')}`
          return formatted
        }),
      ),
    [quoteState, t],
  )

  const networkCostState = useMemo(
    () =>
      pipe(
        quoteState,
        AsyncStateN.map((it) => {
          return match(it)
            .with(
              { network: P.union(Network.Ethereum, Network.Base) },
              (it) => {
                // TODO: Revalidate
                const totalGasPrice =
                  SuggestedGasSettings.calculateTransferMaxFeeInGwei(
                    it.gasPrices.variants.market,
                  )
                return (
                  <>
                    <Numeric
                      value={totalGasPrice}
                      preset="currency-amount-short"
                      currency="GWEI"
                    />
                  </>
                )
              },
            )
            .with({ network: P.union(Network.Solana, Network.Ton) }, (it) => {
              return (
                <>
                  $
                  <Numeric
                    value={it.networkCostUsd}
                    preset="currency-amount-short"
                  />
                </>
              )
            })
            .exhaustive()
        }),
      ),
    [quoteState],
  )

  const priceImpactState = useMemo(
    () =>
      pipe(
        quoteState,
        AsyncStateN.map((it) => {
          const formattedCost = Numbro(it.priceImpact).format({
            trimMantissa: true,
            thousandSeparated: true,
            forceSign: true,
          })
          return <>{formattedCost}%</>
        }),
      ),
    [quoteState],
  )

  const minimumReceiveState = useMemo(
    () =>
      pipe(
        quoteState,
        AsyncStateN.map((it) => {
          return (
            <>
              <Numeric
                value={it.minimumReceive}
                preset="currency-amount-short"
                currency={quoteCurrency.ticker}
              />
            </>
          )
        }),
      ),
    [quoteCurrency.ticker, quoteState],
  )

  return {
    averageWaitTimeState,
    networkCostState,
    priceImpactState,
    minimumReceiveState,
  }
}
